<script setup>
import { reactive } from 'vue';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();

const form = reactive({
  channelName: '',
  corpId: '',
  secret: '',
  openKfId: '',
  encodingAesKey: '',
  token: '',
});

const rules = {
  channelName: { required },
  corpId: { required },
  secret: { required },
  openKfId: { required },
  encodingAesKey: { required },
  token: { required },
};

const v$ = useVuelidate(rules, form);

const uiFlags = store.getters['inboxes/getUIFlags'];

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    const wechatKfChannel = await store.dispatch('inboxes/createChannel', {
      name: form.channelName?.trim(),
      channel: {
        type: 'wechat_kf',
        corp_id: form.corpId,
        secret: form.secret,
        open_kf_id: form.openKfId,
        encoding_aes_key: form.encodingAesKey,
        token: form.token,
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: wechatKfChannel.id,
      },
    });
  } catch (error) {
    useAlert(
      error.message ||
        t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.DESC')"
    />
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model="form.channelName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
            "
            @blur="v$.channelName.$touch"
          />
          <span v-if="v$.channelName.$error" class="message">{{
            $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.CHANNEL_NAME.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.corpId.$error }">
          {{ $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.CORP_ID.LABEL') }}
          <input
            v-model="form.corpId"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.CORP_ID.PLACEHOLDER')
            "
            @blur="v$.corpId.$touch"
          />
          <span v-if="v$.corpId.$error" class="message">{{
            $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.CORP_ID.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.secret.$error }">
          {{ $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.SECRET.LABEL') }}
          <input
            v-model="form.secret"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.SECRET.PLACEHOLDER')
            "
            @blur="v$.secret.$touch"
          />
          <span v-if="v$.secret.$error" class="message">{{
            $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.SECRET.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.openKfId.$error }">
          {{ $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.OPEN_KF_ID.LABEL') }}
          <input
            v-model="form.openKfId"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.OPEN_KF_ID.PLACEHOLDER')
            "
            @blur="v$.openKfId.$touch"
          />
          <span v-if="v$.openKfId.$error" class="message">{{
            $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.OPEN_KF_ID.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.encodingAesKey.$error }">
          {{ $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.ENCODING_AES_KEY.LABEL') }}
          <input
            v-model="form.encodingAesKey"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.ENCODING_AES_KEY.PLACEHOLDER')
            "
            @blur="v$.encodingAesKey.$touch"
          />
          <span v-if="v$.encodingAesKey.$error" class="message">{{
            $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.ENCODING_AES_KEY.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.token.$error }">
          {{ $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.TOKEN.LABEL') }}
          <input
            v-model="form.token"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.TOKEN.PLACEHOLDER')
            "
            @blur="v$.token.$touch"
          />
          <span v-if="v$.token.$error" class="message">{{
            $t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.TOKEN.ERROR')
          }}</span>
        </label>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.WECHAT_KF_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
